// src/pages/DashboardPage.tsx
import React, { useState } from "react";
import { InputProvider } from "../components/context/InputGlobal/InputContext";
import Sidebar from "../components/Sidebar/Sidebar";
import { Bar, Doughnut } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import mapaDistribuicao from "../assets/mapapng.png";

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement,
} from "chart.js";
import { useDashboard } from "../hooks/useDashboard";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  ChartDataLabels,
  BarElement,
  Title,
  PointElement,
  LineElement
);

const DashboardPage: React.FC = () => {
  const { Dashboard, isLoading } = useDashboard();

  const doughnutData = {
    labels: Dashboard?.doughnut?.labels || [],
    datasets: [
      {
        data: Dashboard?.doughnut?.data || [],
        backgroundColor: ["#4CAF50", "#F44336"],
        borderWidth: 1,
      },
    ],
  };

  const barData = {
    labels: Dashboard?.bar?.labels || [],
    datasets:
      Dashboard?.bar?.datasets.map((dataset: any) => ({
        ...dataset,
        backgroundColor: "#78E01D",
      })) || [],
  };

  const maxValue = Math.max(...(barData.datasets[0]?.data || [0]));
  const yAxisMax = Math.ceil(maxValue * 1.1);

  const inferenciaNegadasData = {
    labels: [
      "Dispositivo",
      "Documento com Risco",
      "FaceMatch",
      "Erro ao Consultar",
      "CPF Analfabeto",
      "Foto Manipulada",
      "Foto do Documento",
      "Foto de Foto",
      "CPF com Óbito",
    ],
    datasets: [
      {
        data: [5, 4, 3, 3, 3, 2, 2, 2, 1],
        backgroundColor: "#78E01D",
        maxBarThickness: 44,
      },
    ],
  };

  const motivosRevisaoData = {
    labels: [
      "Avaliar Documento",
      "Documento não Aceito",
      "Documento Ilegível",
      "Documento Fotocopiado",
      "Menor de Idade",
      "Foto de Foto",
      "CPF Divergente",
      "Avaliar Selfie",
    ],
    datasets: [
      {
        data: [7, 2, 2, 2, 1, 1, 1, 1],
        backgroundColor: "#78E01D",
        maxBarThickness: 24,
      },
    ],
  };

  return (
    <div
      className="d-flex"
      style={{ minHeight: "100vh", backgroundColor: "#000" }}
    >
      <Sidebar />
      <InputProvider>
        <div
          className="flex-grow-1 p-4"
          style={{
            backgroundColor: "#ffffff",
            borderTopLeftRadius: "40px",
            borderBottomLeftRadius: "40px",
            padding: "32px",
            boxShadow: "0 0 12px rgba(0,0,0,0.05)",
          }}
        >
          <h1
            style={{
              color: "#78E01D",
              fontWeight: "600",
              fontSize: "36px",
              marginBottom: "24px",
            }}
          >
            Dashboard
          </h1>

          {/* Cards */}
          {!isLoading && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "16px",
                marginBottom: "32px",
              }}
            >
              {[
                { title: "Total de Validações", value: "1.632" },
                { title: "Taxa de Aprovadas", value: "97,4%" },
                { title: "Taxa de Negadas", value: "1,5%" },
                { title: "Pendente Revisão", value: "1,1%" },
                { title: "Tempo Médio Análise", value: "04:24" },
              ].map((item, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: "#e2e2e2",
                    borderRadius: "8px",
                    padding: "16px 24px",
                    minWidth: "180px",
                    flex: 1,
                    textAlign: "center",
                    boxShadow: "0 0 4px rgba(0,0,0,0.1)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <p
                    style={{
                      fontWeight: "bold",
                      fontSize: "14px",
                      marginBottom: "8px",
                      color: "#000",
                    }}
                  >
                    {item.title}
                  </p>
                  <p
                    style={{
                      color: "#78E01D",
                      fontSize: "24px",
                      fontWeight: "bold",
                    }}
                  >
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Grid de 3 colunas com gráficos e mapa */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "24px",
              marginTop: "48px",
            }}
          >
            {/* Gráfico 1: Inferência de Negadas */}
            <div
              style={{
                backgroundColor: "#e2e2e2",
                borderRadius: "12px",
                padding: "16px",
                height: "100%",
              }}
            >
              <h4
                style={{
                  textAlign: "center",
                  marginBottom: "16px",
                  color: "#000",
                  fontWeight: "bold",
                }}
              >
                Inferência de Negadas
              </h4>
              <Bar
                data={inferenciaNegadasData}
                options={{
                  indexAxis: "y",
                  plugins: {
                    legend: { display: false },
                    datalabels: {
                      color: "#000",
                      anchor: "end",
                      align: "right",
                      font: { weight: "bold", size: 14 },
                    },
                  },
                  scales: {
                    x: {
                      beginAtZero: true,
                      ticks: { stepSize: 0, color: "#000" },
                    },
                    y: {
                      ticks: { color: "#000" },
                    },
                  },
                  elements: {
                    bar: {
                      borderRadius: 4,
                    },
                  },
                }}
              />
            </div>

            {/* Gráfico 2: Motivos de Revisão */}
            <div
              style={{
                backgroundColor: "#e2e2e2",
                borderRadius: "12px",
                padding: "16px",
                height: "100%",
              }}
            >
              <h4
                style={{
                  textAlign: "center",
                  marginBottom: "16px",
                  color: "#000",
                  fontWeight: "bold",
                }}
              >
                Motivos de Revisão
              </h4>
              <Bar
                data={motivosRevisaoData}
                options={{
                  indexAxis: "y",
                  plugins: {
                    legend: { display: false },
                    datalabels: {
                      color: "#000",
                      anchor: "end",
                      align: "right",
                      font: { weight: "bold", size: 14 },
                    },
                  },
                  scales: {
                    x: {
                      beginAtZero: true,
                      ticks: { stepSize: 0, color: "#000" },
                    },
                    y: {
                      ticks: { color: "#000" },
                    },
                  },
                  elements: {
                    bar: {
                      borderRadius: 4,
                      //@ts-ignore
                      barThickness: 48,
                    },
                  },
                }}
              />
            </div>

            {/* Mapa */}
            <div
              style={{
                backgroundColor: "#e2e2e2",
                borderRadius: "12px",
                padding: "16px",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <h4
                style={{
                  textAlign: "center",
                  marginBottom: "16px",
                  color: "#000",
                  fontWeight: "bold",
                }}
              >
                Mapa de Distribuição
              </h4>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <img
                  src={mapaDistribuicao}
                  alt="Mapa de Distribuição"
                  style={{
                    maxWidth: "100%",
                    height: "318px",
                    borderRadius: "8px",
                  }}
                />
              </div>
            </div>
            <div className="p-4 flex">
                <div
                  style={{
                    backgroundColor: "#e2e2e2",
                    borderRadius: "12px",
                    padding: "16px",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <h4
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      color: "#000",
                      fontWeight: "bold",
                    }}
                  >
                    Aprovações vs Reprovações
                  </h4>

                  <div style={{ width: "200px", height: "280px" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "24px",
                        marginTop: "16px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            width: "16px",
                            height: "16px",
                            backgroundColor: "#4CAF50",
                            borderRadius: "4px",
                          }}
                        ></div>
                        <span style={{ color: "#000" }}>Aprovadas</span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            width: "16px",
                            height: "16px",
                            backgroundColor: "#F44336",
                            borderRadius: "4px",
                          }}
                        ></div>
                        <span style={{ color: "#000" }}>Reprovadas</span>
                      </div>
                    </div>
                    <Doughnut
                      data={doughnutData}
                      options={{
                        maintainAspectRatio: false,
                        plugins: {
                          datalabels: {
                            color: "#000",
                            font: {
                              weight: "bold",
                              size: 16,
                            },
                            formatter: (value: number) => value,
                          },
                          legend: {
                            display: false,
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
          </div>

          {/* Gráficos */}
          {isLoading ? (
            <p style={{ textAlign: "center" }}>Carregando dados...</p>
          ) : (
            <div
              className="d-flex"
              style={{
                alignItems: "center",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >

              {/* Bar Chart */}
              <div className="p-4 flex">
                <div
                  style={{
                    backgroundColor: "#e2e2e2",
                    borderRadius: "12px",
                    padding: "16px",
                    height: "100%",
                  }}
                >
                  <h4
                    className="mb-4"
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      color: "#000",
                      fontWeight: "bold",
                    }}
                  >
                    Consultas por dia
                  </h4>
                  <div style={{ width: "500px", height: "400px" }}>
                    <Bar
                      data={barData}
                      options={{
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            max: yAxisMax,
                            ticks: { color: "#000" },
                          },
                          x: {
                            ticks: { color: "#000" },
                          },
                        },
                        plugins: {
                          legend: {
                            display: false,
                          },
                          datalabels: {
                            anchor: "end",
                            align: "top",
                            color: "#000",
                            font: { weight: "bold" },
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </InputProvider>
    </div>
  );
};

export default DashboardPage;
